// src/services/api.js — Complete API Service Layer v7
import { API_URL, API_ORIGIN } from "../config.js";
const BASE_URL = API_URL;

// P34c (2026-05-24): PRODUCTION-GRADE asset origin.
// Backend owns /store static assets (auras, frames) — production architecture.
// Derive backend origin from BASE_URL by stripping the trailing '/api'.
// This way webapp + mobile both fetch /store/* from the SAME backend origin.
//
// Example:
//   BASE_URL    = 'http://localhost:5000/api'
//   API_ORIGIN  = 'http://localhost:5000'
//   <img src='http://localhost:5000/store/auras/halo.svg' />
//
// In production:
//   BASE_URL    = 'https://api.papercrypto.app/api'
//   API_ORIGIN  = 'https://api.papercrypto.app'
//   <img src='https://api.papercrypto.app/store/auras/halo.svg' />
// API_ORIGIN now imported from config.js

// P34c (2026-05-24): Resolve a relative /store URL to backend's absolute URL.
// Safe for already-absolute URLs (http://, https://) — returns them unchanged.
// Safe for null/undefined — returns it as-is.
//
// Usage in components:
//   <img src={toAbsoluteStoreUrl(frame.image_url)} alt="" />
export function toAbsoluteStoreUrl(url) {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;     // already absolute
  if (url.startsWith('/store/')) return API_ORIGIN + url;
  return url;                                     // unknown pattern, return as-is
}

const getToken   = () => localStorage.getItem('pc_token');
const setToken   = (t) => localStorage.setItem('pc_token', t);
const clearToken = () => localStorage.removeItem('pc_token');

async function request(method, path, body, auth = false) {
  // S-010 (Rule O): Send X-Client-Platform header so backend can apply
  // AdSense-compliant logic for webapp (no ad-gates) vs AdMob logic for mobile.
  // Critical for AdSense policy compliance — webapp must NEVER receive AD_REQUIRED responses.
  const headers = { 'Content-Type': 'application/json', 'X-Client-Platform': 'web' };
  if (auth) { const t = getToken(); if (t) headers['Authorization'] = `Bearer ${t}`; }
  try {
    const res  = await fetch(`${BASE_URL}${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined });
    let data;
    try { data = await res.json(); } catch { data = { message: 'Server error' }; }
    // Handle rate limiting gracefully — NEVER treat as session expiry, NEVER crash
    if (res.status === 429) {
      // Return a soft error object instead of throwing — prevents React ErrorBoundary crash
      return { success: false, _rateLimit: true, message: data.message || 'Too many requests. Please wait a moment.' };
    }
    // Session expiry detection:
    // - NEVER fire on /auth/login, /auth/register (these are credential checks)
    // - ONLY fire when user was logged in AND token is actually invalid/expired
    const isAuthEndpoint = path.includes('/auth/login') || path.includes('/auth/register') ||
                           path.includes('/auth/forgot') || path.includes('/auth/reset');
    const hasToken = !!localStorage.getItem('pc_token');
    // Only clear token on real JWT/session errors — NOT plain 401 (e.g. not-logged-in)
    // S-109: guard toLowerCase — some endpoints (e.g. DM send) legitimately return
    // `message` as an OBJECT (the created message), not a string. Calling
    // .toLowerCase() on an object crashed with "toLowerCase is not a function".
    // Only treat a STRING message as potential error text.
    const errText = (typeof data?.message === 'string' ? data.message : '').toLowerCase();
    const isTokenError = errText.includes('invalid token') ||
                         errText.includes('jwt') ||
                         errText.includes('token expired') ||
                         errText.includes('session expired') ||
                         (data?.code || '') === 'TOKEN_EXPIRED';
    if (!isAuthEndpoint && hasToken && res.status === 401 && isTokenError) {
      // v136: Auto-refresh — try refresh token before forcing re-login
      const rt = localStorage.getItem('pc_refresh');
      if (rt && !path.includes('/auth/refresh')) {
        try {
          const refreshRes = await fetch(`${BASE_URL}/auth/refresh`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: rt }),
          });
          const refreshData = await refreshRes.json();
          if (refreshData.success && refreshData.token) {
            localStorage.setItem('pc_token', refreshData.token);
            if (refreshData.refreshToken) localStorage.setItem('pc_refresh', refreshData.refreshToken);
            // v225: Notify socket to re-auth with new token (position_update events keep arriving)
            try { window.dispatchEvent(new CustomEvent('pc_token_refreshed', { detail: { token: refreshData.token } })); } catch(_) {}
            // Retry original request with new token
            return request(method, path, body, auth);
          }
        } catch {}
      }
      clearToken(); localStorage.removeItem('pc_refresh');
      window.dispatchEvent(new CustomEvent('pc_auth_expired'));
      throw new Error('Session expired. Please login again.');
    }
    if (!res.ok) throw new Error(data.message || 'Request failed');
    return data;
  } catch (e) { throw e; }
}

const get  = (path, a)       => request('GET',    path, null, a);
const post = (path, body, a) => request('POST',   path, body, a);
const put  = (path, body, a) => request('PUT',    path, body, a);
const patch = (path, body, a) => request('PATCH', path, body, a);
const del  = (path, a)       => request('DELETE', path, null, a);

// ─── Auth ─────────────────────────────────────────────────
export const auth = {
  register:        async (data) => { const r = await post('/auth/register', data); if (r.success && r.token) { setToken(r.token); if (r.refreshToken) localStorage.setItem('pc_refresh', r.refreshToken); } return r; },
  sendEmailOtp:    (email)       => post('/auth/email/send-otp',   { email }),
  verifyEmailOtp:  (email, otp)  => post('/auth/email/verify-otp', { email, otp }),
  login:    async (data) => { const r = await post('/auth/login', data); if (r.success && r.token) { setToken(r.token); if (r.refreshToken) localStorage.setItem('pc_refresh', r.refreshToken); window.dispatchEvent(new CustomEvent('pc_logged_in')); } return r; },
  logout:         ()             => { clearToken(); localStorage.removeItem('pc_refresh'); },
  refresh:  async ()             => { const rt = localStorage.getItem('pc_refresh'); if (!rt) return { success:false }; const r = await post('/auth/refresh', { refreshToken: rt }); if (r.success && r.token) { setToken(r.token); if (r.refreshToken) localStorage.setItem('pc_refresh', r.refreshToken); } return r; },
  me:             ()             => get('/auth/me', true),
  updateProfile:  (data)         => put('/auth/profile', data, true),
  changePassword: (data)         => post('/auth/change-password', data, true),
  deleteAccount:  (data)         => request('DELETE', '/auth/account', data, true),
  getSettings:    ()             => get('/auth/settings', true),
  saveSettings:   (data)         => put('/auth/settings', data, true),
  forgotPassword: (email)        => post('/auth/forgot-password', { email }),
  verifyResetToken:(token)       => post('/auth/verify-reset-token', { token }),
  resetPassword:  (token, pwd)   => post('/auth/reset-password', { token, new_password: pwd }),
  uploadAvatar: async (file) => {
    const form = new FormData(); form.append('avatar', file);
    return fetch(`${BASE_URL}/auth/avatar`, { method:'POST', headers:{ Authorization:`Bearer ${getToken()}` }, body:form }).then(r => r.json());
  },
  isLoggedIn: () => !!getToken(),
};

// ─── Market ───────────────────────────────────────────────
export const market = {
  prices:     ()                    => get('/market/prices'),
  price:      (id)                  => get(`/market/price/${id}`),
  chart:      (id, days)            => get(`/market/chart/${id}?days=${days || 1}`),
  candles:    (symbol, interval, startTime, endTime) => {
    let q = `symbol=${symbol}&interval=${interval||'1h'}`;
    if (startTime) q += `&startTime=${startTime}`;
    if (endTime)   q += `&endTime=${endTime}`;
    return get(`/market/candles?${q}`);
  },
  search:     (q)                   => get(`/market/search?q=${encodeURIComponent(q)}`),
  trending:   ()                    => get('/market/trending'),
  news:       ()                    => get('/market/news'),
  global:     ()                    => get('/market/global'),
  orderbook:  (symbol)              => get(`/exchange/orderbook/${symbol}`),
  whales:     (symbol, limit = 50)  => get(`/market/whales?${symbol?'symbol='+symbol+'&':''}limit=${limit}`),
  // S-054 (LOCKED — 2026-05-10): Heatmap Advanced — Option D consumers.
  // Backend tier-gates each via requireFeature(...) middleware. Frontend
  // wraps HeatmapAdvanced component in PremiumGate for desire-first UX.
  heatmapMultitf:    ()  => get('/market/heatmap/multitf', true),
  heatmapSectors:    ()  => get('/market/heatmap/sectors', true),
  heatmapBreakouts:  ()  => get('/market/heatmap/breakouts', true),
  heatmapAllCoins:   ()  => get('/market/heatmap/all-coins', true),
  // S-053 (LOCKED — 2026-05-10): Whale Tracker Advanced — Option D consumers.
  // Backend tier-gates each endpoint via requireFeature(...) middleware.
  // Frontend wraps WhaleAdvanced component in PremiumGate for desire-first UX.
  whaleAggregate:  (symbol)         => get(`/market/whales/aggregate${symbol?'?symbol='+symbol:''}`, true),
  whalePressure:   (symbol)         => get(`/market/whales/pressure-ratio${symbol?'?symbol='+symbol:''}`, true),
  whaleHistorical: (symbol, days=7) => get(`/market/whales/historical?days=${days}${symbol?'&symbol='+symbol:''}`, true),
  whaleAlertsList: ()               => get('/market/whales/alerts', true),
  whaleAlertCreate:(body)           => post('/market/whales/alerts', body, true),
  whaleAlertDelete:(id)             => del(`/market/whales/alerts/${id}`, true),
  cvd:        (symbol)              => get(`/market/cvd/${symbol}`),
  cvdAnalysis:(symbol)              => get(`/market/cvd/${symbol}/analysis`),
  aiInsight:  (symbol, interval='1h') => get(`/market/ai-insight/${symbol}?interval=${interval}`),
  // S-293 — send the auth token (Rule Q with mobile). Same 401 blackout applied here.
  unifiedSignal: (symbol, interval='1h') => get(`/market/unified-signal/${symbol}?interval=${interval}`, true),
  fomo:          ()                    => get('/market/fomo'),
  signal:        (symbol='BTC', iv='1h') => get(`/market/signal?symbol=${symbol}&interval=${iv}`, true),
  insight:       (symbol='BTC')          => get(`/market/insight?symbol=${symbol}`, true),
  cvdMultiTF: (symbol)              => get(`/market/cvd/${symbol}/multitf`),
  cvdHeatmap: (symbol, minutes=30)  => get(`/market/cvd/${symbol}/heatmap?minutes=${minutes}`),
  movers:     (n = 10)              => get(`/market/movers?n=${n}`),
  heatmap:    ()                    => get('/market/heatmap'),
  leaderboard:(period = 'alltime', limit = 50) => get(`/market/leaderboard?period=${period}&limit=${limit}`),
  leaderboardMe: ()                             => get('/market/leaderboard/me', true),
  fundingRates: ()                  => get('/market/funding'),
};

// ─── Trading ──────────────────────────────────────────────
export const trading = {
  // SINGLE SOURCE OF TRUTH — all UI must use this for counts/PnL/balance
  userStats:    ()            => get('/trading/user-stats', true),
  placeOrder:   (data)        => post('/trading/order', data, true),
  orders:       (params = {}) => { const q = new URLSearchParams(params).toString(); return get(`/trading/orders${q ? '?'+q : ''}`, true); },
  positions:    ()            => get('/trading/positions', true),
  portfolio:    ()            => get('/trading/portfolio', true),
  pnl:          ()            => get('/trading/pnl', true),
  watchlist:    ()            => get('/trading/watchlist', true),
  addWatch:     (symbol)      => post('/trading/watchlist', { symbol }, true),
  removeWatch:  (symbol)      => del(`/trading/watchlist/${symbol}`, true),
  awardAdTrade: ()            => post('/trading/award-ad-trade', {}, true),
  deleteOrder:  (id)          => del(`/trading/orders/${id}`, true),
  exitOrder:     (id, body)    => post(`/trading/exit/${id}`, body||{}, true),
  closePosition: (positionId, body)  => post('/trading/close-position', { positionId, ...(body||{}) }, true),
  updateSlTp:    (data)              => post('/trading/update-sltp', data, true),
  closeAllPositions: ()       => post('/derivatives/close-all', {}, true),

  // v186: Advanced limit orders — IOC / FOK / Post-Only / GTC time-in-force
  placeLimit:    (data)              => post('/trading/limit', data, true),
  modifyLimit:   (orderId, data)     => patch(`/trading/limit/${orderId}`, data, true),

  // v186: OCO orders — One-Cancels-the-Other (limit + stop linked)
  placeOCO:      (data)              => post('/trading/oco', data, true),
  listOCO:       (symbol)            => get(`/trading/oco/${symbol}`, true),
  cancelOCO:     (orderId)           => del(`/trading/oco/${orderId}`, true),
};

// ─── Derivatives / Futures Positions ─────────────────────
export const derivatives = {
  positions:          ()      => get('/derivatives/positions', true),
  open:               (data)  => post('/derivatives/open', data, true),
  close:              (posId) => post(`/derivatives/close/${posId}`, {}, true),
  leverageInfo:       ()      => get('/derivatives/leverage-info'),
  stats:              ()      => get('/derivatives/stats', true),
  closeAllPositions:  ()      => post('/derivatives/close-all', {}, true),
};

// ─── Wallet (four-currency: Currency $, Diamonds 💎, Coins 🪙, Coins) ───
// v189 POLICY: PaperCrypto is paper-trading — no deposit/withdraw/reset methods.
// Balance is managed through: rewards, store purchases, diamond packs, in-app Currency earning.
export const wallet = {
  get:         ()     => get('/wallet', true),
  currency:    ()     => get('/wallet/currency',  true),
  diamonds:    ()     => get('/wallet/diamonds',  true),
  coins:       ()     => get('/wallet/coins',     true),
  buyCurrency: (data) => post('/wallet/currency/buy-with-diamonds', data, true),
  buyDiamonds: (data) => post('/wallet/diamonds/purchase',          data, true),
};

// ─── Subscriptions ────────────────────────────────────────
export const subscriptions = {
  // S-031b: Pass auth=true so backend can read user's profile country (Issue 1+2 root cause)
  plans:       () => get('/subscriptions/plans', true),
  create:      (data) => post('/subscriptions/create', data, true),
  verify:      (data) => post('/subscriptions/verify', data, true),
  current:     ()     => get('/subscriptions/current', true),
  features:    ()     => get('/subscriptions/features', true),
  checkAccess: (feature) => get(`/subscriptions/check-access?feature=${feature}`, true),
};

// ─── Alerts ───────────────────────────────────────────────
export const alerts = {
  list:   () => get('/alerts', true),
  create: (data) => post('/alerts', data, true),
  toggle: (id, is_active) => put(`/alerts/${id}`, { is_active }, true),
  delete: (id) => del(`/alerts/${id}`, true),
};

// ─── Forums ───────────────────────────────────────────────
export const labs = {
  // Clubs
  // S-041 (2026-05-10) Fix #1: Added auth=true. Backend /labs/clubs uses
  // optionalAuth — without JWT, req.user is undefined → my_status returns
  // null for ALL clubs regardless of actual membership. This was the REAL
  // root cause of "+ Join not flipping to ✅ Joined" reported across S-037,
  // S-038, S-039, S-040 — pre-existing bug exposed by S-040's orphan
  // cleanup migration (which correctly removed reqMap fallback but couldn't
  // populate memSet because JWT wasn't being sent).
  clubs:          ()           => get('/labs/clubs', true),
  joinClub:       (club_id)    => post('/labs/clubs/join', { club_id }, true),
  createClub:     (data)       => post('/labs/clubs/create', data, true), // v199b
  clubMembers:    (id)         => get(`/labs/clubs/${id}/members`),
  // v236: Competitions API helpers removed (B+ plan — feature deprecated)
  // Activity feed
  feed:            (limit=30)  => get(`/labs/feed?limit=${limit}`, true),
  addFeed:         (data)      => post('/labs/feed', data, true),
  notifications:   (limit=20)  => get(`/labs/notifications?limit=${limit}`, true),
  ads:             ()           => get('/labs/ads'),
  trackAdClick:    (id)         => post(`/labs/ads/${id}/click`, {}),
};

// ─── v230: Clubs detail + posts + media + moderation ───────────────────────
// Wired to /api/social/clubs/* endpoints. Used by the ClubDetailPage.
export const clubs = {
  // Detail page data
  detail:       (clubId, page=1) => get(`/social/clubs/${clubId}?page=${page}`, true),
  // Posts inside a club
  createPost:   (clubId, payload) => post(`/social/clubs/${clubId}/posts`, payload, true),
  deletePost:   (clubId, postId)  => del(`/social/clubs/${clubId}/posts/${postId}`, true),
  likePost:     (clubId, postId)  => post(`/social/clubs/${clubId}/posts/${postId}/like`, {}, true),
  // Membership
  join:         (clubId)          => post(`/social/clubs/${clubId}/join`, {}, true),
  leave:        (clubId)          => post(`/social/clubs/${clubId}/leave`, {}, true),
  // Moderation (admin/owner only)
  removeMember: (clubId, userId)  => del(`/social/clubs/${clubId}/members/${userId}`, true),
  banMember:    (clubId, userId, reason) => post(`/social/clubs/${clubId}/ban/${userId}`, { reason }, true),
  deleteClub:   (clubId)          => del(`/social/clubs/${clubId}`, true),
  // v231: Owner-only moderation
  editClub:     (clubId, updates) => patch(`/social/clubs/${clubId}`, updates, true),
  setRole:      (clubId, userId, role) => post(`/social/clubs/${clubId}/members/${userId}/role`, { role }, true),
  // v232 Session C: My clubs sidebar list
  myClubs:      ()                => get('/social/clubs-my', true),
  // Media upload (10MB image / 50MB video)
  upload: async (file, kind) => {
    const fd = new FormData();
    fd.append('kind', kind);
    fd.append('file', file);
    const r = await fetch(`${BASE_URL}/social/clubs/upload`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getToken()}` },
      body: fd,
    });
    return r.json();
  },
};

// v199c2a: Direct Messaging API — wired to v199c1 backend endpoints
// v228: Upload + attachment + unread-count support
export const messages = {
  list:        ()                       => get('/messages/conversations', true),
  open:        (other_user_id)          => post('/messages/conversations', { other_user_id }, true),
  history:     (convId, before)         => get(`/messages/conversations/${convId}/messages${before ? `?before=${encodeURIComponent(before)}` : ''}`, true),
  // v228: send accepts optional attachment fields
  send:        (convId, payload)        => {
    // Backward-compatible: if payload is a string, treat as text-only message
    const body = typeof payload === 'string' ? { content: payload } : payload;
    return post(`/messages/conversations/${convId}/messages`, body, true);
  },
  markRead:    (convId)                 => post(`/messages/conversations/${convId}/read`, {}, true),

  // v228: Upload attachment (multipart). `kind` must be one of: image, video, voice, file
  upload: async (file, kind, extraMeta = {}) => {
    const fd = new FormData();
    fd.append('kind', kind);
    fd.append('file', file);
    if (extraMeta.duration_ms != null) fd.append('duration_ms', String(extraMeta.duration_ms));
    const r = await fetch(`${BASE_URL}/messages/upload`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getToken()}` },  // no Content-Type — browser sets multipart boundary
      body: fd,
    });
    return r.json();
  },

  // v228: Total unread count across all conversations (for header badge)
  unreadCount: () => get('/messages/unread-count', true),
};

export const forums = {
  posts:       (page = 1)    => get(`/forums/posts?page=${page}`, true),
  createPost:  (data)        => post('/forums/posts', data, true),
  // v229: Upload image (10MB) or video (50MB) for a forum post
  upload: async (file, kind) => {
    const fd = new FormData();
    fd.append('kind', kind);
    fd.append('file', file);
    const r = await fetch(`${BASE_URL}/forums/upload`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${getToken()}` },
      body: fd,
    });
    return r.json();
  },
  likePost:    (id)          => post(`/forums/posts/${id}/like`, {}, true),
  savePost:    (id)          => post(`/forums/posts/${id}/save`, {}, true),
  saved:       ()            => get('/forums/saved', true),
  list:        ()            => get('/forums/list'),
  joinForum:   (id)          => post(`/forums/${id}/join`, {}, true),
  follow:      (uid)         => post(`/forums/follow/${uid}`, {}, true),
  getComments: (postId)      => get(`/forums/posts/${postId}/comments`, true),
  addComment:  (postId, text)=> post(`/forums/posts/${postId}/comments`, { content: text }, true),
  // Social actions (wired to /api/social/* routes)
  deletePost:   (id)              => del(`/social/posts/${id}`, true),
  editPost:     (id, content)     => put(`/social/posts/${id}`, { content }, true),
  deleteComment:(id)              => del(`/social/comments/${id}`, true),
  editComment:  (id, content)     => put(`/social/comments/${id}`, { content }, true),
  replyComment: (postId, content, parentId) => post(`/social/posts/${postId}/reply`, { content, parent_id: parentId }, true),
  reportContent:(type, id, reason) => post('/social/report', { target_type: type, target_id: id, reason }, true),
  // S-117: user blocking (Google Play UGC policy — report + block required)
  blockUser:    (userId)          => post('/social/block', { target_user_id: userId }, true),
  unblockUser:  (userId)          => post('/social/unblock', { target_user_id: userId }, true),
  getBlocked:   ()                => get('/social/blocked', true),
  getUserProfile:(userId)         => get(`/social/profile/${userId}`, true),
  getUserPosts:(userId)           => get(`/social/profile/${userId}/posts`, true),
  requestClub: (name)        => post('/forums/clubs/request', { club_name: name }, true),
};

// ─── Notifications ────────────────────────────────────────
export const notifications = {
  list:    (unread) => get(`/notifications${unread ? '?unread_only=true' : ''}`, true),
  readAll: ()       => put('/notifications/read-all', {}, true),
  read:    (id)     => put(`/notifications/${id}/read`, {}, true),
};

// ─── Support ──────────────────────────────────────────────
export const support = {
  submitTicket: (data) => post('/support/ticket', data, true),
  tickets:      ()     => get('/support/tickets', true),
  feedback:     (data) => post('/support/feedback', data, true),
};

// ─── Payments ─────────────────────────────────────────────
export const payments = {
  razorpayOrder:  (data) => post('/payment/razorpay/create-order', data, true),
  razorpayVerify: (data) => post('/payment/razorpay/verify', data, true),
  paypalOrder:    (data) => post('/payment/paypal/create-order', data, true),
  paypalCapture:  (data) => post('/payment/paypal/capture', data, true),
  stripeCheckout: (data) => post('/payment/stripe/checkout', data, true),
  payuInitiate:   (data) => post('/payment/payu/initiate', data, true),
  payuVerify:     (data) => post('/payment/payu/verify', data, true),
  history:        ()     => get('/payment/history', true),
};

// ─── 2FA ──────────────────────────────────────────────────
// ── 2FA API ─── REMOVED v275 (P-008) ─────────────────────────
// v275 (P-008 — Doubt #125): Entire `twoFA` export DELETED.
// Verified ZERO consumers across webapp/src (no static imports, no dynamic
// imports via `await import()`). SettingsPage.jsx uses raw `fetch()` calls
// directly to /2fa/* endpoints instead of this helper. Future refactor could
// migrate SettingsPage to use a 2FA helper, but that's a separate session.
// All 6 methods (status, setup, enable, disable, verify, regenBackup) dead.

// ─── Auth Extensions ──────────────────────────────────────
export const authExtra = {
  googleLogin:     (id_token) => post('/auth/google', { id_token }),
  // G1.7: Apple Sign-In — receives identity_token + optional user_info (name on first sign-in)
  appleLogin:      (identity_token, user_info) => post('/auth/apple', { identity_token, user_info }),
  // G1.4: Twitter/Facebook/Instagram/Threads OAuth removed (see backend/routes/_deprecated/socialAuth.js)
  // Following Binance-level standard: Email + Phone + Google + Apple only.
  verify2FA:   (tmp, code)=> post('/2fa/verify', { temp_token:tmp, totp_code:code }),
};

// ─── Copy Trading ─────────────────────────────────────────
export const copyTrading = {
  leaderboard:  (period = 'all') => get('/copy/leaderboard?period=' + period),
  trader:       (id)             => get('/copy/trader/' + id),
  follow:       (id, data)       => post('/copy/follow/' + id, data, true),
  unfollow:     (id)             => del('/copy/unfollow/' + id, true),
  myFollowing:  ()               => get('/copy/my-following', true),
  myCopiers:    ()               => get('/copy/my-copiers', true),
  myCopyTrades: ()               => get('/copy/my-copy-trades', true),
};

const apiModule = { auth, market, trading, subscriptions, alerts, forums, notifications, support, getToken, clearToken };
export default apiModule;

// ── Algo Simulation API ─────────────────────────────────
// ── Algo Trading API ─────────────────────────────────────────
// 🚨 INTENTIONAL — DO NOT DELETE — DYNAMIC IMPORT CONSUMER 🚨
// v275 (P-008 Rule L Phase 2 catch): This export is LIVE but invisible to
// static grep. AlgoToolsTab.jsx uses `const { algo } = await import('../../services/api')`
// at lines 160, 221, 244 (dynamic import / code splitting pattern).
// Future dead-code cleanup sessions: BEFORE deleting any api.js export,
// always run BOTH static grep (`import.*\bX\b.*services/api`) AND dynamic
// import grep (`await import.*services/api` + `\\{ X \\}`) to avoid breakage.
// Backend route /api/algo (server.js:983) is also live — supports algo bots.
export const algo = {
  strategies: ()                    => get('/algo/strategies'),
  start:      (body)                => post('/algo/start', body, true),
  stop:       ()                    => post('/algo/stop', {}, true),
  status:     ()                    => get('/algo/status', true),
  history:    (limit = 50)          => get(`/algo/history?limit=${limit}`, true),
};

// ── Gamification API ─────────────────────────────────────────
export const gamification = {
  progress:      ()                    => get('/gamification/progress', true),
  dailyClaim:    ()                    => post('/gamification/daily-claim', {}, true),
  calendarDefs:  ()                    => get('/gamification/daily-rewards-calendar'),
  challenges:    ()                    => get('/gamification/challenges', true),
  challengeProg: (challengeId, inc=1)  => post('/gamification/challenge-progress', { challengeId, increment:inc }, true),
  awardCoins:       (action, meta={})     => post('/gamification/award-coins', { action, meta }, true),
  coinsLeaderboard: ()                    => get('/gamification/coins-leaderboard'),
  psychology:    ()                    => get('/gamification/psychology', true),
  journal:       (limit=20)            => get(`/gamification/journal?limit=${limit}`, true),
};

// ── Social API v134 ──────────────────────────────────────────
// ── Social API ─── REMOVED v275 (P-008) ──────────────────────
// v275 (P-008 — Doubt #125): Entire `social` export DELETED.
// 100% Rule H violation — every method had a canonical equivalent:
//   social.editPost      → forums.editPost
//   social.deletePost    → forums.deletePost
//   social.editComment   → forums.editComment
//   social.deleteComment → forums.deleteComment
//   social.replyToPost   → forums.replyComment
//   social.report        → forums.reportContent
//   social.getProfile    → forums.getUserProfile
//   social.getClubs/getClub/createClub/joinClub/deleteClub/clubPost → clubs.*
//   social.removeMember/banMember → clubs.removeMember/banMember
// Verified ZERO consumers (static + dynamic import) across webapp/src.
// Trading endpoints (orderPreview/orderHistory/livePnL/feeSchedule) were
// never wired up — backend doesn't expose /trading/preview either.

// ── Economy API v135 ─── REMOVED v275 (P-003) ────────────────
// v275 (P-003 — Doubt #121β): Entire `economy` export DELETED.
// All 16 methods (transactions, shopItems, buyItem, checkItem, inventory,
// equipItem, unequipItem, myProfile, viewProfile, setCountry, dividends,
// globalBoard, countryBoard, weeklyBoard, coinBoard, friendsBoard) verified
// ZERO live consumers project-wide via Rule J Q1 UPSTREAM grep audit.
//
// Replacements / canonical alternatives:
//   • Wallet → wallet.get() (line 167) — already migrated G4.25a
//   • Shop → use store endpoints (storeAPI on mobile, no webapp shop UI yet)
//   • Inventory → use store endpoints
//   • Profile → social.getProfile() (line 401)
//   • Leaderboards → market.leaderboard() / market.leaderboardMe()
//
// Backend routes /api/economy/* remain mounted (server.js:956) — deployed
// mobile users may still hit them. Backend deletion deferred to PENDING P-009
// (post-mobile-rollout cleanup, same pattern as P-005).
// Per Rule K research (Pensero 2026): paper-trail doc comment preserved for
// git archaeology — small validated deletion is safer than sweeping removal.

// ── Intelligence API v275 (S-051c parity restoration) ───────
// S-051c (LOCKED — 2026-05-10): Restored `intelligence` namespace —
// MINIMAL surface (only `premiumSignal`). Closes Rule Q WISDOM parity gap
// where mobile had Premium Signal Of The Day but webapp had no consumer of
// /api/intelligence/premium-signal endpoint. Mobile keeps `unlockSignalAd`
// (rewarded video ad → 1hr unlock); webapp does NOT export it because Rule O
// forbids incentivized-ad CTAs on AdSense-served pages (instant-ban policy).
// Webapp free users see PremiumGate desire-first paywall → upgrade only.
//
// NOTE on v275 (P-003) prior deletion: that removed 7 OTHER methods (nudges,
// upgradeRec, suggestions, achievementCard, postTrade, firstOpen, dashboard)
// which had ZERO live consumers. premiumSignal was NEVER part of that set —
// its endpoint exists for the very feature being parity-aligned here.
export const intelligence = {
  premiumSignal: () => get('/intelligence/premium-signal', true),
};

// ── Ads API v136 ─────────────────────────────────────────────
export const ads = {
  config:            ()                => get('/ads/config', true),
  canShow:           ()                => get('/ads/can-show', true),
  nonce:             ()                => get('/ads/nonce', true),
  impression:        (data)            => post('/ads/impression', data, true),
  click:             (data)            => post('/ads/click', data, true),
  rewardComplete:    (nonce, platform) => post('/ads/reward/complete', { nonce, platform }, true),
  interstitialTrigger: ()              => post('/ads/interstitial/trigger', {}, true),
  feed:              (limit=5, ctx)    => get(`/ads/feed?limit=${limit}${ctx ? '&context='+ctx : ''}`),
  stats:             ()                => get('/ads/stats', true),
  setConfig:         (key, value)      => put(`/ads/config/${key}`, { value }, true),
  // Feature unlock via ad
  featureStatus:     (feature)         => get(`/ads/feature-status?feature=${feature}`, true),
  unlockFeature:     (feature, nonce)  => post('/ads/unlock-feature', { feature, nonce }, true),
  unlockableFeatures: ()               => get('/ads/unlockable-features', true),
  // Loss recovery
  lossRecovery:      ()                => get('/ads/loss-recovery', true),
  claimRecovery:     (nonce)           => post('/ads/loss-recovery/claim', { nonce }, true),
  // Context-aware ad decision
  shouldShow:        (context)         => get(`/ads/should-show?context=${context}`, true),
  // Double profit hook
  doubleProfit:      ()                => get('/ads/double-profit', true),
  claimDouble:       (nonce)           => post('/ads/double-profit/claim', { nonce }, true),
};

// ═══════════════════════════════════════════════════════════════════
// v186: Admin API — all 17 new admin endpoints from the v186 revival
// ═══════════════════════════════════════════════════════════════════
export const admin = {
  // User management (formerly-broken endpoints now working)
  stats:            ()              => get('/admin/stats', true),
  users:            (q)             => get(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`, true),
  userDetails:      (id)            => get(`/admin/users/${id}`, true),
  userStats:        (id)            => get(`/admin/users/${id}/stats`, true),
  banUser:          (id, reason)    => post(`/admin/users/${id}/ban`, { reason }, true),
  unbanUser:        (id)            => post(`/admin/users/${id}/unban`, {}, true),
  upgradeUserPlan:  (id, plan)      => post(`/admin/users/${id}/upgrade`, { plan }, true),
  resetPortfolio:   (id, balance)   => post(`/admin/users/${id}/reset-portfolio`, { balance }, true),
  warnUser:         (id, reason)    => post(`/admin/users/${id}/warn`, { reason }, true),

  // Market control — halt/resume/circuit breaker
  haltMarket:       (symbol, reason) => post('/admin/market/halt',   { symbol, reason }, true),
  resumeMarket:     (symbol)         => post('/admin/market/resume', { symbol }, true),
  marketStates:     ()               => get('/admin/market/states', true),
  marketHalted:     (symbol)         => get(`/admin/market/halted/${symbol}`, true),

  // Dead Letter Queue — failed trades
  dlqList:          (userId)         => get(`/admin/dlq/${userId}`, true),
  dlqRetry:         ()               => post('/admin/dlq/retry', {}, true),
  dlqResolve:       (id)             => post(`/admin/dlq/${id}/resolve`, {}, true),

  // Liquidity & token management
  addToken:         (symbol, name)   => post('/admin/tokens/add', { symbol, name }, true),
  getTokens:        ()               => get('/admin/tokens', true),
  injectLiquidity:  (symbol, bidAmount, askAmount) => post('/admin/liquidity/inject',       { symbol, bidAmount, askAmount }, true),
  toggleMarketMaker:(symbol, enable, spreadPct)    => post('/admin/liquidity/market-maker', { symbol, enable, spreadPct }, true),
  spreadAnalysis:   (symbol, bid, ask) => get(`/admin/liquidity/spread/${symbol}?bid=${bid}&ask=${ask}`, true),
  imbalanceCheck:   (symbol)         => get(`/admin/liquidity/imbalance/${symbol}`, true),

  // Scaling & cost
  scalingConfig:    ()               => get('/admin/scaling', true),
  costInfo:         (users=10000)    => get(`/admin/cost?users=${users}`, true),

  // Targeted per-symbol Binance streams
  startStream:      (symbol)         => post('/admin/stream/start', { symbol }, true),
  stopStream:       (symbol)         => post('/admin/stream/stop',  { symbol }, true),
  activeStreams:    ()               => get('/admin/stream/active', true),

  // Pre-existing
  reports:          (status='pending') => get(`/admin/reports?status=${status}`, true),
  reportAction:     (id, action, reason) => post(`/admin/reports/${id}/action`, { action, reason }, true),
  systemHealth:     ()               => get('/admin/system/health', true),
};

// ═══════════════════════════════════════════════════════════════════════
// v195c: LOCALIZED PRICING API — calls v195b backend pricing routes
// ═══════════════════════════════════════════════════════════════════════
export const pricing = {
  // S-031b: Pass auth=true so backend can read user's profile country (Issue 1+2 root cause)
  localized:    ()                   => get('/pricing/localized', true),
  country:      ()                   => get('/pricing/country', true),
  rates:        ()                   => get('/pricing/rates'),
  item:         (id, type='offer')   => get(`/pricing/item/${id}?type=${type}`, true),
  refreshRates: ()                   => post('/pricing/rates/refresh', {}, true),
};

// ═══════════════════════════════════════════════════════════════════════
// v196b2: CURRENCY BUNDLES API — diamond-spend purchases for $ Currency
// ═══════════════════════════════════════════════════════════════════════
export const currencyBundles = {
  list: ()             => get('/store/currency-bundles'),
  buy:  (bundle_id)    => post('/store/currency-bundles/buy', { bundle_id }, true),
};
